import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import {
  DocumentQueryPagination,
  DocumentQueryParams,
  FieldDataType,
  Filter,
  FilterGroup,
  FilterGroupType,
  RuleOperator,
  SchemaField
} from 'src/app/modules/core/store/models';

import { MapDrawingService } from '../../drawing/map-drawing.service';
import { DataApiService } from '../../services/data/data-api.service';
import { DEFAULT_MAX_DOCUMENT_COUNT, MapLayerQueryBuildService } from '../../services/data/map-layer-query-build.service';
import { TableColumnsService } from '../../services/table/table-columns-service';
import * as copActions from '../cop/cop.actions';
import * as copSelectors from '../cop/cop.selectors';
import { DocumentsToDelete, Layer, LayerDocuments, LayerTypes } from '../cop/models';
import * as rsdActions from '../rsd/rsd.actions';
import * as rsdSelectors from '../rsd/rsd.selectors';
import * as listsActions from './lists.actions';
import * as listsSelectors from './lists.selectors';
import { Card, CardView, ListQuery, MAX_CARDS_TO_LOAD } from './models/card.model';
import { ListDataService } from '../../services/list-data.service';
import { MapApiService } from '../../services';
import { DatasetSubtype, DatasetType } from 'src/app/modules/core/store/models/dataset.model';
import { cloneDeep } from 'lodash-es';
import { ListsTableType } from './models/table-type.enum';
import { DrawingToolsService } from '../../drawing/drawing-tools.service';

@Injectable()
export class ListsEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<any>,
    private dataApiService: DataApiService,
    private translate: TranslateService,
    private mapDrawingService: MapDrawingService,
    private mapDrawingTools: DrawingToolsService,
    private tableColumnsService: TableColumnsService,
    private mapApiService: MapApiService,
    private listDataService: ListDataService
  ) {}

  // loads layer documents for one or all layers
  // called when layer color/button is clicked
  loadLayersDocumentsForLists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.loadLayersDocumentsForLists),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectLayerQuickFilterSelections),
        this.store$.select(copSelectors.selectMapPosition)
      ),
      switchMap(([action, mapState, quickFilters, mapPosition]) => {
        const layerUIds = [];

        action.layerUIds.forEach(l => {
          const layers = Object.values(mapState.layersSet).filter(ls => ls.uId === l || !l);
          layers.forEach(layer => {
            if (!layerUIds.find(l => l === layer.uId)) {
              layerUIds.push(layer.uId);
            }
          });
        });

        const name = layerUIds.length === 1 ? mapState.layersSet[layerUIds[0]].name : this.translate.instant('EDIT-PANEL.MAP-CLUSTER');
        const colorCode = layerUIds.length === 1 ? mapState.layersSet[layerUIds[0]].color : null;

        const cardsQuery: ListQuery = {
          updateDate: new Date().getTime().toString(),
          dataUpdateTime: {},
          data: {
            type: null,
            headerLabel: name,
            colorCode: colorCode,
            layerQueries: {}
          }
        };

        layerUIds.forEach(l => {
          const layer = mapState.layersSet[l];
          const dataset = mapState.datasetsSet[layer?.datasetUId];

          const floorFilter = MapLayerQueryBuildService.generateFloorFilter(layer, dataset, this.mapDrawingService.floor);
          let query = MapLayerQueryBuildService.buildLayerQueryParams(layer, dataset, quickFilters[l], floorFilter, mapPosition);
          query.fields = null;
          query.pagination.take = layer.type === LayerTypes.HorizonLocationIntelRatings ? 10000 : MAX_CARDS_TO_LOAD + 10;
          query.sort = 'date_created desc';

          cardsQuery.data.type = 'layers';
          cardsQuery.data.layerQueries[l] = {
            query: query,
            documents: [],
            requestedTotalCount: MapLayerQueryBuildService.autoClusterDocuments
              ? MapLayerQueryBuildService.maxDocumentCount
              : (layer.displayOptions?.maxItemsToRender ?? DEFAULT_MAX_DOCUMENT_COUNT)
          };
        });

        return [listsActions.loadDocumentsFromApiIntoCards({ query: cardsQuery, name, colorCode })];
      })
    )
  );

  // loads layer documents from workflow summary
  // called when workflow summary color/button is clicked
  loadSummaryDocumentsForLists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.loadSummaryDocumentsForLists),
      withLatestFrom(
        this.store$.select(copSelectors.selectSelectedMapState),
        this.store$.select(copSelectors.selectLayerQuickFilterSelections),
        this.store$.select(copSelectors.selectMapPosition)
      ),
      switchMap(([action, mapState, quickFilters, mapPosition]) => {
        const summary = action.summaryForStatus;
        const incomingByLayer = action.summaryForStatus.layerDocs;

        const cardsQuery: ListQuery = {
          updateDate: new Date().getTime().toString(),
          dataUpdateTime: {},
          data: {
            type: null,
            headerLabel: summary.name,
            colorCode: summary.colorCode,
            sectionUId: action.sectionUId,
            summaryName: summary.name,
            layerQueries: {}
          }
        };

        incomingByLayer.forEach(ld => {
          const layer = mapState.layersSet[ld.layerUId];
          const dataset = mapState.datasetsSet[layer?.datasetUId];

          const floorFilter = MapLayerQueryBuildService.generateFloorFilter(layer, dataset, this.mapDrawingService.floor);

          let workflowGroup = {
            groupType: FilterGroupType.And,
            filters: [
              {
                field: 'workflow_step_uid',
                operator: RuleOperator.Equal,
                value: summary.workflowStepUIds[ld.layerUId]
              }
            ]
          } as FilterGroup;

          let query = MapLayerQueryBuildService.buildLayerQueryParams(layer, dataset, quickFilters[ld.layerUId], floorFilter, mapPosition);
          query.fields = null;
          query.pagination.take = MAX_CARDS_TO_LOAD + 10;
          query.sort = 'date_created desc';

          if (!query?.filterGroup) {
            query.filterGroup = {
              filters: [],
              filterGroups: [workflowGroup],
              groupType: FilterGroupType.And
            };
          } else {
            let deepClone = JSON.parse(JSON.stringify(query));

            if (!!deepClone.filterGroup) {
              workflowGroup.filterGroups = [deepClone.filterGroup];
            }

            deepClone.filterGroup = workflowGroup;
            query = deepClone;
          }

          if (action.userClick === false) {
            cardsQuery.dataUpdateTime[ld.layerUId] = new Date().getTime();
          }
          cardsQuery.data.type = 'summary';
          cardsQuery.data.layerQueries[ld.layerUId] = {
            query: query,
            documents: [],
            requestedTotalCount: MapLayerQueryBuildService.autoClusterDocuments
              ? MapLayerQueryBuildService.maxDocumentCount
              : (layer.displayOptions?.maxItemsToRender ?? DEFAULT_MAX_DOCUMENT_COUNT)
          };
        });

        return [listsActions.loadDocumentsFromApiIntoCards({ query: cardsQuery, name: summary.name, colorCode: summary.colorCode })];
      })
    )
  );

  // loads layer documents from cluster of document ids
  // called when cluster marker is clicked
  loadClusterDocumentsForLists$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.loadClusterDocumentsForLists),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const data = {
          type: null,
          headerLabel: this.translate.instant('EDIT-PANEL.MAP-CLUSTER'),
          colorCode: null,
          layerQueries: {},
          clusterId: action.clusterId
        };

        if (Object.keys(action.layerDocuments).length === 1) {
          const layer = mapState?.layersSet[Object.keys(action.layerDocuments)[0]] ?? null;
          const dataset = mapState?.datasetsSet[layer.datasetUId] ?? null;

          data.colorCode = layer?.color ?? null;

          if (
            dataset?.type === DatasetType.Camera &&
            dataset?.subtype === DatasetSubtype.Traffic &&
            action?.clusterData['isCameraCluster']
          ) {
            data.headerLabel = this.translate.instant('EDIT-PANEL.CO-LOCATED-CAMERAS');
            data.colorCode = '#5C940D';
          }
        }

        const cardsQuery: ListQuery = {
          updateDate: new Date().getTime().toString(),
          dataUpdateTime: {},
          data
        };

        Object.keys(action.layerDocuments).forEach(l => {
          const datasetUId = mapState.layersSet[l]?.datasetUId;
          if (!!datasetUId) {
            cardsQuery.data.type = 'cluster';
            cardsQuery.data.layerQueries[l] = {
              clusterPosition: action.position,
              query: null,
              documents: action.layerDocuments[l],
              requestedTotalCount: action.layerDocuments[l].length
            };
          }
        });

        if (!!action.zoomToBounds) {
          this.mapDrawingService.fitToBounds(action.zoomToBounds);
        }

        const { headerLabel: name, colorCode } = data;
        return [listsActions.loadDocumentsFromApiIntoCards({ query: cardsQuery, name, colorCode })];
      })
    )
  );

  private getFilterFunction(subtype: DatasetSubtype): (f: SchemaField) => boolean {
    switch (subtype) {
      case DatasetSubtype.HorizonLocationIntelRatings:
        const ratingsFilter = (f: SchemaField) =>
          f.dataType === FieldDataType.Text && (f.systemName === 'name' || f.systemName === 'country_name');
        return ratingsFilter;
      case DatasetSubtype.HorizonGeospatialThreatZone:
        const threatZonesFilter = (f: SchemaField) => f.dataType === FieldDataType.Text && f.systemName === 'location_name';
        return threatZonesFilter;
      case DatasetSubtype.Transports:
        const transportsFilter = (f: SchemaField) =>
          f.dataType === FieldDataType.Text &&
          f.systemName !== 'position_list' &&
          f.systemName !== 'alarm_list' &&
          f.systemName !== 'segments' &&
          f.systemName !== 'master_gps_battery' &&
          f.systemName !== 'master_gps_signal';
        return transportsFilter;
      default:
        const defaultFilter = (f: SchemaField) => f.dataType === FieldDataType.Text || f.dataType === FieldDataType.AutoIncrement;
        return defaultFilter;
    }
  }

  // Generates layer documents from given api query
  loadDocumentsFromApiIntoCards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.loadDocumentsFromApiIntoCards),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const listQuery = action.query;

        if (!!!listQuery) {
          return [];
        }

        const forkActions = [];
        const layers: Layer[] = [];

        let totalCount = 0;
        let countTotals = true;

        Object.keys(listQuery.data.layerQueries).forEach(l => {
          const layer = mapState.layersSet[l];
          const dataset = mapState.datasetsSet[layer?.datasetUId];

          layers.push(layer);

          let sortNames = MapLayerQueryBuildService.getDatasetLabelFields(dataset);

          let sort = '';
          if (
            dataset?.type !== DatasetType.Alerts &&
            dataset?.type !== DatasetType.Incident &&
            dataset?.type !== DatasetType.Alarm &&
            dataset?.subtype !== DatasetSubtype.GenetecEvent &&
            !!sortNames
          ) {
            const sortParts = [];
            sortNames.map(s => sortParts.push(`${s} asc`));
            sort = sortParts.join(',');
          } else if (dataset?.type === DatasetType.Alerts) {
            sort = 'date_updated desc';
          } else {
            sort = 'date_created desc';
          }

          let searchFilterGroup = null;
          if (!!action.searchTerm && action.searchTerm.trim().length) {
            const filters = [] as Filter[];
            const searchTerm = action.searchTerm.trim();

            const filterFn = this.getFilterFunction(dataset.subtype);

            let textFields = dataset.schema?.fields?.filter(filterFn);

            // Remove flight positions from query since it is not indexed
            if (dataset.subtype === DatasetSubtype.Flight) {
              textFields = textFields.filter(f => f.systemName !== 'flight_positions');
            }

            textFields.forEach(f => {
              filters.push({
                field: f.systemName,
                operator: RuleOperator.ContainsNoCase,
                value: searchTerm
              });
            });

            searchFilterGroup =
              filters.length > 0
                ? {
                    groupType: FilterGroupType.Or,
                    filters: filters,
                    filterGroups: []
                  }
                : null;
          }

          let query = this.listDataService.getQuery(listQuery, l, sort, null, null, null, null, searchFilterGroup);

          if (listQuery.data?.type === 'cluster') {
            countTotals = false;
            // count unique document uids
            totalCount += listQuery.data?.layerQueries[l].documents?.filter((d, index) => {
              return listQuery.data?.layerQueries[l].documents.indexOf(d) === index;
            })?.length;
          }

          if (!!dataset) {
            const apiCall =
              dataset.subtype === DatasetSubtype.HorizonLocationIntelRatings ||
              dataset.subtype === DatasetSubtype.HorizonGeospatialThreatZone
                ? 'getHorizonIntelRatingsDocuments'
                : 'getDocuments';

            forkActions.push(
              this.dataApiService[apiCall](dataset?.uId, query, true).pipe(catchError(err => of(copActions.setError({ err }))))
            );
          }
        });

        const results: LayerDocuments = {};
        const autoClusterMode = MapLayerQueryBuildService.autoClusterDocuments;

        if (!forkActions.length) {
          this.mapDrawingTools.stopDrawingForSearch();
          return [
            listsActions.setListQueryToStore({ query: listQuery }),
            listsActions.loadLayerDocumentsIntoCards({
              clusterId: listQuery.data.clusterId,
              layerDocuments: results,
              listType: 'documents',
              totalCount: 0,
              name: action.name,
              colorCode: action.colorCode
            })
          ];
        }

        return forkJoin(forkActions).pipe(
          mergeMap(data => {
            data.forEach((d, i) => {
              results[layers[i].uId] = (d as any)?.data;

              if (countTotals) {
                const dataCount = (d as any)?.totalCount ?? 0;
                const maxItems = layers[i].displayOptions?.maxItemsToRender ?? DEFAULT_MAX_DOCUMENT_COUNT;

                totalCount += autoClusterMode || dataCount < maxItems ? dataCount : maxItems;
              }
            });
            this.mapDrawingTools.stopDrawingForSearch();
            return [
              listsActions.setListQueryToStore({ query: listQuery }),
              listsActions.loadLayerDocumentsIntoCards({
                clusterId: listQuery.data.clusterId,
                layerDocuments: results,
                listType: 'documents',
                totalCount: totalCount,
                name: action.name,
                colorCode: action.colorCode
              })
            ];
          })
        );
      })
    )
  );

  // creates cardview/cards from layer documents
  loadLayerDocumentsIntoCards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.loadLayerDocumentsIntoCards),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState), this.store$.select(listsSelectors.selectTableVisible)),
      switchMap(([action, mapState, isTableVisible]) => {
        const documentsByLayer: LayerDocuments = action.layerDocuments;

        var cards: Card[] = [];

        let name = action.name;
        let color = action.colorCode;

        if (!name) {
          if (Object.keys(documentsByLayer).length === 1) {
            name = mapState.layersSet[Object.keys(documentsByLayer)[0]]?.name;
          }
        }
        if (!color) {
          if (Object.keys(documentsByLayer).length === 1) {
            color = mapState.layersSet[Object.keys(documentsByLayer)[0]]?.color;
          }
        }
        if (!name) {
          name = 'topo-clusters';
        }
        if (!color) {
          color = '#7257ff';
        }

        // flat the layer documents structure
        let allDocs = [];

        Object.keys(documentsByLayer)?.forEach(l => {
          documentsByLayer[l]?.forEach(d => {
            allDocs.push({ layerUId: l, document: d });
          });
        });

        // re-sort the layer documents if needed
        Object.keys(documentsByLayer)?.forEach(l => {
          if (documentsByLayer[l]?.length > 1) {
            const layer = mapState.layersSet[Object.keys(documentsByLayer)[0]];
            const dataset = mapState.datasetsSet[layer?.datasetUId];
            allDocs = allDocs.sort((a, b) => {
              if (!!a.document.date_updated && !!b.document.date_updated) {
                return a.document.date_updated > b.document.date_updated ? -1 : 1;
              }
              if (!!a.document.date_updated) {
                return a.document.date_updated > b.document.date_created ? -1 : 1;
              }
              if (!!b.document.date_updated) {
                return a.document.date_created > b.document.date_updated ? -1 : 1;
              }
              return a.document.date_created > b.document.date_created ? -1 : 1;
            });
          }
        });

        allDocs?.forEach(ld => {
          const layer = mapState.layersSet[ld.layerUId];
          const dataset = mapState.datasetsSet[layer?.datasetUId];
          const view = dataset?.workflow?.views.find(vw => vw.name === 'card');

          const workflowStep = dataset?.workflow?.workflowSteps?.find(step => step.uId === ld.document.workflow_step_uid);
          cards.push({
            document: ld.document,
            layer: layer,
            dataset: dataset,
            workflowColor: workflowStep ? workflowStep.statusHtmlColorCode : action.colorCode,
            workflowName: workflowStep ? workflowStep.statusName : action.name,
            view: view
          } as Card);
        });

        const cardView = {
          summaryName: name,
          htmlColorCode: color,
          cards: cards,
          totalCount: action.totalCount,
          clusterId: action.clusterId,
          listType: action.listType
        } as CardView;

        return [
          listsActions.setCardDataToStore({
            cardView: cardView
          })
        ];
      })
    )
  );

  updateFloorFilterValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.updateFloorFilterValue),
      withLatestFrom(this.store$.select(listsSelectors.selectListQuery), this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, listQuery, mapState]) => {
        if (!!!action.value || !!!listQuery) {
          return [];
        }

        let updatedQuery = false;

        const updatedListQuery = cloneDeep(listQuery);

        Object.keys(updatedListQuery.data?.layerQueries).forEach(l => {
          const layer = mapState?.layersSet[l];
          const dataset = mapState?.datasetsSet[layer?.datasetUId];

          if (!!layer?.displayOptions?.floorFilterSourceField) {
            const floorFieldName = layer.displayOptions?.floorFilterSourceField;
            const floorField = dataset?.schema.fields.find(f => f.systemName === floorFieldName);
            const floorFieldValue = floorField?.picklistValues.find(p => p.value === action.value);
            if (!!floorFieldValue) {
              const query = updatedListQuery.data.layerQueries[l].query;
              if (!!query) {
                const filter = MapLayerQueryBuildService.findFilterByFieldName(query.filterGroup, floorFieldName);
                if (!!filter) {
                  if (filter.value !== action.value) {
                    updatedQuery = true;
                    filter.value = floorFieldValue.uId;
                  }
                }
              }
            }
          }
        });

        if (updatedQuery) {
          return [listsActions.setListQueryToStore({ query: updatedListQuery })];
        } else {
          return [];
        }
      })
    )
  );

  showTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.showTable),
      withLatestFrom(this.store$.select(listsSelectors.selectCardViewForDisplay)),
      switchMap(([, cardView]) => {
        const type: ListsTableType = !!cardView ? cardView.listType : ListsTableType.Documents;
        return [listsActions.showTableToStore({ tableType: type })];
      })
    )
  );

  showCards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.showCards),
      withLatestFrom(this.store$.select(listsSelectors.selectCardViewForDisplay)),
      switchMap(([, cardView]) => {
        const actions = [];
        if (cardView?.listType === 'documents') {
          actions.push(listsActions.reloadAndShowCards());
        } else {
          actions.push(listsActions.showCardsToStore());
        }
        return actions;
      })
    )
  );

  hideTableShowCardsIfDataLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.hideTableShowCardsIfDataLoaded),
      withLatestFrom(
        this.store$.select(listsSelectors.selectCardViewForDisplay),
        this.store$.select(rsdSelectors.selectSelectedDocumentState)
      ),
      switchMap(([, cardView, selectedDocument]) => {
        const actions = [];
        actions.push(listsActions.hideTableToStore());
        if (!!selectedDocument) {
          actions.push(rsdActions.showDrawer());
        } else if (cardView?.cards?.length > 0) {
          if (cardView?.listType === 'documents') {
            actions.push(listsActions.reloadAndShowCards());
          } else {
            if (!!!selectedDocument) {
              actions.push(rsdActions.showDrawer());
            }
            actions.push(listsActions.showCardsToStore());
          }
        }
        return actions;
      })
    )
  );

  cardClicked$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.cardClicked),
      switchMap(action => {
        return [
          listsActions.setCardVisibilityToStore({ isVisible: false }),
          rsdActions.setSelectedDocument({
            layerUId: action.layerUId,
            documentUId: action.document.uid,
            isDrawerOpen: true,
            replaceStack: true
          })
        ];
      })
    )
  );

  toggleShowCardsOrTableForLayerClick$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.toggleShowCardsOrTableForLayerClick),
      withLatestFrom(
        this.store$.select(listsSelectors.selectTableVisible),
        this.store$.select(listsSelectors.selectIsCardsVisible),
        this.store$.select(listsSelectors.selectListQuery)
      ),
      switchMap(([action, isTableVisible, isCardVisible, listQuery]) => {
        // Never hide the table, if card view and same layer clicked twice then hide
        if (!action.reload && isCardVisible && !isTableVisible && !!listQuery?.data?.layerQueries[action.layer.uId]) {
          return [listsActions.hideCardsToStore(), rsdActions.hideDrawer({ clearDocumentStack: true })];
        }

        // By default on click, show the card view
        if (!isTableVisible && !isCardVisible) {
          return [listsActions.showCardsToStore(), rsdActions.showDrawer()];
        }
        return [];
      })
    )
  );

  cancelCardView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.cancelCardView),
      withLatestFrom(this.store$.select(rsdSelectors.selectSelectedDocumentState)),
      switchMap(([action, selectedDocument]) => {
        return [
          copActions.clearSelectedDocumentsToStore(),
          rsdActions.hideDrawer({ clearDocumentStack: true }),
          listsActions.clearCardsToStore()
        ];
      })
    )
  );

  reloadAndShowCards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.reloadAndShowCards),
      withLatestFrom(this.store$.select(listsSelectors.selectListQuery), this.store$.select(listsSelectors.selectCardViewForDisplay)),
      switchMap(([action, listQuery, cardView]) => {
        return [
          listsActions.loadDocumentsFromApiIntoCards({
            query: listQuery,
            name: cardView.summaryName,
            colorCode: cardView.htmlColorCode
          }),
          rsdActions.showDrawer(),
          listsActions.showCardsToStore()
        ];
      })
    )
  );

  loadExportTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.loadExportTemplates),
      switchMap(action => {
        return this.mapApiService.getDatasetsExportTemplates(action.datasetUIds).pipe(
          map(res => listsActions.loadExportTemplatesCompleteToStore({ results: res })),
          catchError(err => of(copActions.setError({ err })))
        );
      })
    )
  );

  transitionLayerDocumentsToWorkflowStep$ = createEffect(() =>
    this.actions$.pipe(
      ofType(listsActions.transitionLayerDocumentsToWorkflowStep),
      withLatestFrom(this.store$.select(copSelectors.selectSelectedMapState)),
      switchMap(([action, mapState]) => {
        const actions = [];

        Object.keys(action.layerDocuments).forEach(l => {
          const layer = mapState.layersSet[l];
          const dataset = mapState.datasetsSet[layer.datasetUId];

          const destStep = dataset?.workflow?.workflowSteps.find(ws => ws.statusName == action.statusName);
          if (!!destStep) {
            actions.push(
              copActions.transitionDocumentWorkflows({
                layerUId: l,
                workflowStepUId: destStep.uId,
                documents: action.layerDocuments[l],
                datasetUId: dataset.uId
              })
            );
          }
        });

        return actions;
      })
    )
  );
}
